import clsx from 'clsx'
import { Link } from '@tanstack/react-router'
import { useState, useMemo, createContext, DependencyList, ReactNode, useContext, useEffect } from 'react'
import { useAuth } from '@/auth/AuthContext'
import { Can, useAccessContext } from '@/auth/access'
import { canAccessUsersSection, canAccessCompaniesSection, canAccessStaffSection, isSystemContext } from '@tandem/schema/permissions'
import $ from './Main.css'
import Logo from '@tandem/ui/Тандем.svg?react'
import Dropdown from '@tandem/ui/Dropdown'
import md5 from 'md5'
import { Building2Icon, HandshakeIcon, PackageIcon, Users2Icon, UsersRoundIcon } from 'lucide-react'

type CompanyLogo = NonNullable<NonNullable<ReturnType<typeof useAuth>['company']>['config']>['logo']

export default function Layout({ children }) {
  let { user, logout, company, companies, setActiveCompanyId } = useAuth()
  let userEmail = user?.email || ''
  let gravatarUrl = getGravatarUrl(userEmail)
  let inSystemContext = isSystemContext(useAccessContext())
  let companyLogo = company?.config?.logo ?? null

  let [menuOpen, setMenuOpen] = useState(false)
  let [sidecar, setSidecar] = useState<ReactNode>(null)
  let sidecarContextValue = useMemo(() => ({ content: sidecar, setContent: setSidecar }), [sidecar])

  return (
    <AsideContext.Provider value={sidecarContextValue}>
      <div class={$.layout}>
        
        <header class={$.header}>
          <div class={$.logoWrapper}>{/* Side elements of header have flex-grow:1 making SVG centerified w/o wrapper. */}
            <Link to='/'>
              <Logo class={$.logo} />
            </Link>
          </div>
          
          <ul class={$.nav}>
            <Can check={canAccessUsersSection}>
              <li>
                <Link to='/users' activeProps={{ className: $.active }} className={clsx($.link, $.system)}>
                  <Users2Icon />
                  <span>Пользователи</span>
                </Link>
              </li>
            </Can>
            <Can check={canAccessCompaniesSection}>
              <li>
                <Link to='/companies' activeProps={{ className: $.active }} className={clsx($.link, $.system)}>
                  <Building2Icon />
                  <span>Компании</span>
                </Link>
              </li>
            </Can>
            {company?.features?.includes('catalog') && (
              <li>
                <Link to='/catalog' activeProps={{ className: $.active }} className={$.link}>
                  <PackageIcon />
                  <span>Каталог</span>
                </Link>
              </li>
            )}
            <Can check={canAccessStaffSection}>
              <li>
                <Link to='/staff' activeProps={{ className: $.active }} className={$.link}>
                  <UsersRoundIcon />
                  <span>Сотрудники</span>
                </Link>
              </li>
            </Can>
            {company?.features?.includes('proexpert') && (
              <li>
                <Link to='/proexpert' activeProps={{ className: $.active }} className={$.link}>
                  <HandshakeIcon />
                  <span>Контрагенты</span>
                </Link>
              </li>
            )}
          </ul>

          <div class={$.authArea}>
            {companyLogo && <ActiveCompanyLogo logo={companyLogo} />}
            <Dropdown
              open={menuOpen}
              onOpenChange={setMenuOpen}
              align='left'
              verticalAlign='bottom'
              trigger={
                <button class={$.userMenuTrigger} type='button'>
                  <img src={gravatarUrl} class={clsx($.userpic, inSystemContext && $.system)} />
                </button>
              }
              class={$.authDropdown}
            >
              <div class={$.userMenu}>
                {companies.length > 0 && (
                  <>
                    {companies.map((c) => (
                      <button
                        key={c.company_id}
                        type='button'
                        class={clsx(
                          $.menuOption,
                          c.company_id === company?.company_id && $.menuOptionActive,
                        )}
                        onClick={() => {
                          setActiveCompanyId(c.company_id)
                          setMenuOpen(false)
                        }}
                      >
                        <span>{c.name}</span>
                      </button>
                    ))}
                    <hr class={$.menuDivider} />
                  </>
                )}
                <Link to='/settings' className={$.menuOption} activeProps={{ className: $.menuOptionActive }} onClick={() => setMenuOpen(false)}>
                  Настройки
                </Link>
                <button
                  class={$.menuOption}
                  type='button'
                  onClick={() => {
                    logout()
                    setMenuOpen(false)
                  }}
                >
                  Выйти
                </button>
              </div>
            </Dropdown>
          </div>
        </header>

        <div class={$.body}>
          <main class={$.content}>{children}</main>
          {sidecar && <aside class={$.sidecar}>{sidecar}</aside>}
        </div>

        <footer class={$.footer}>© 2026 ООО «Тандем Ритейл»</footer>
      </div>
    </AsideContext.Provider>
  )
}

function ActiveCompanyLogo({ logo }: { logo: CompanyLogo }) {
  if (!logo) return null

  let cap = Math.min(Math.max(logo.cap ?? 0, 0), 0.99)
  let baseline = Math.min(Math.max(logo.baseline ?? 1, cap + 0.01), 1)
  let visibleSpan = baseline - cap
  let height = 32 / visibleSpan
  let top = -cap * height

  return (
    <div class={$.activeCompanyLogo} aria-label='Логотип активной компании'>
      <img
        src={logo.url}
        alt=''
        style={{
          height: `${height}px`,
          top: `${top}px`
        }}
      />
    </div>
  )
}


export let AsideContext = createContext<{
  content: ReactNode
  setContent: (node: ReactNode) => void
} | null>(null)


export function useAside(renderContent: () => ReactNode, deps: DependencyList) {
  let context = useContext(AsideContext)
  useEffect(() => {
    context?.setContent(renderContent())
    return () => context?.setContent(null)
  }, deps || [])
}


function getGravatarUrl(email: string, size = 80) {
  let hash = md5(email.toLowerCase().trim())
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=identicon`
}
